// MetroTable.js
import React from "react";
import { Table } from "antd";
import FilterSlider from "./FilterSlider";
import { formatNumberWithCommas } from "../../helpers/formatters";

const MetroTable = ({ data, filteredData, activeFilters, onFilterChange }) => {
  const getColumnMinMax = (key) => {
    const values = data
      .map((item) => parseFloat(item[key]))
      .filter((val) => !isNaN(val));
    return [Math.floor(Math.min(...values)), Math.ceil(Math.max(...values))];
  };

  const createFilterDropdown = (dataIndex) => {
    const [min, max] = getColumnMinMax(dataIndex);
    return ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <FilterSlider
        setSelectedKeys={setSelectedKeys}
        selectedKeys={selectedKeys}
        confirm={confirm}
        clearFilters={clearFilters}
        min={min}
        max={max}
        prefix={dataIndex === "median" ? "$" : ""}
        suffix={dataIndex !== "median" ? "%" : ""}
        formatNumber={dataIndex === "median" ? formatNumberWithCommas : null}
      />
    );
  };

  const columns = [
    {
      title: "Metro",
      dataIndex: "metro",
      sorter: (a, b) => a.metro.localeCompare(b.metro),
    },
    {
      title: "Region",
      dataIndex: "region",
      filters: [...new Set(data.map((item) => item.region))].map((region) => ({
        text: region,
        value: region,
      })),
      filteredValue: activeFilters.region || null,
    },
    {
      title: "Last Year Change",
      dataIndex: "lastyearchange",
      render: (value) => `${parseFloat(value)}%`,
      sorter: (a, b) =>
        parseFloat(a.lastyearchange) - parseFloat(b.lastyearchange),
      filterDropdown: createFilterDropdown("lastyearchange"),
      filteredValue: activeFilters.lastyearchange || null,
    },
    {
      title: "Median",
      dataIndex: "median",
      render: (value) => `$${formatNumberWithCommas(parseFloat(value))}`,
      sorter: (a, b) => parseFloat(a.median) - parseFloat(b.median),
      filterDropdown: createFilterDropdown("median"),
      filteredValue: activeFilters.median || null,
    },
    {
      title: "Forecast",
      dataIndex: "forecast",
      render: (value) => `${parseFloat(value)}%`,
      sorter: (a, b) => parseFloat(a.forecast) - parseFloat(b.forecast),
      filterDropdown: createFilterDropdown("forecast"),
      filteredValue: activeFilters.forecast || null,
    },
    {
      title: "Percentage Rising",
      dataIndex: "percentrising",
      render: (value) => `${parseFloat(value)}%`,
      sorter: (a, b) =>
        parseFloat(a.percentrising) - parseFloat(b.percentrising),
      filterDropdown: createFilterDropdown("percentrising"),
      filteredValue: activeFilters.percentrising || null,
    },
    {
      title: "Percentage Falling",
      dataIndex: "percentfalling",
      render: (value) => `${parseFloat(value)}%`,
      sorter: (a, b) =>
        parseFloat(a.percentfalling) - parseFloat(b.percentfalling),
      filterDropdown: createFilterDropdown("percentfalling"),
      filteredValue: activeFilters.percentfalling || null,
    },
    {
      title: "Best Forecast",
      dataIndex: "bestforecast",
      render: (value) => `${parseFloat(value)}%`,
      sorter: (a, b) => parseFloat(a.bestforecast) - parseFloat(b.bestforecast),
      filterDropdown: createFilterDropdown("bestforecast"),
      filteredValue: activeFilters.bestforecast || null,
    },
    {
      title: "Worst Forecast",
      dataIndex: "worstforecast",
      render: (value) => `${parseFloat(value)}%`,
      sorter: (a, b) =>
        parseFloat(a.worstforecast) - parseFloat(b.worstforecast),
      filterDropdown: createFilterDropdown("worstforecast"),
      filteredValue: activeFilters.worstforecast || null,
    },
  ];

  const handleTableChange = (_, filters) => {
    onFilterChange(filters);
  };

  return (
    <div className="responsive-table">
      <Table
        columns={columns}
        dataSource={filteredData}
        onChange={handleTableChange}
        rowKey="metro"
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default MetroTable;
